<template>
  <div>
    <b-modal
      ref="modal"
      title="ตรวจสอบรายการจากเอกสาร (OCR)"
      size="xl"
      dialog-class="ocr-review-dialog"
      ok-title="ยืนยันและบันทึก"
      cancel-title="ยกเลิก"
      :ok-disabled="isSubmitting || lines.length === 0"
      no-close-on-backdrop
      @ok="submit"
      @hidden="reset"
    >
      <div class="ocr-layout" :class="{ 'no-preview': !previewUrl }">
        <!-- the uploaded scan, so every value can be checked against the document beside it -->
        <section v-if="previewUrl" class="ocr-doc">
          <div class="ocr-doc-bar">
            <span class="small font-weight-bold"><i class="fas fa-file-alt mr-1"></i> เอกสารต้นฉบับ</span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <template v-if="previewKind === 'image'">
                <b-button size="sm" variant="light" title="ย่อ" :disabled="zoom <= ZOOM_MIN" @click="zoom -= ZOOM_STEP">
                  <i class="fas fa-search-minus"></i>
                </b-button>
                <span class="small text-muted zoom-label">{{ zoom }}%</span>
                <b-button size="sm" variant="light" title="ขยาย" :disabled="zoom >= ZOOM_MAX" @click="zoom += ZOOM_STEP">
                  <i class="fas fa-search-plus"></i>
                </b-button>
              </template>
              <b-button size="sm" variant="light" :href="previewUrl" target="_blank" rel="noopener" title="เปิดในแท็บใหม่">
                <i class="fas fa-external-link-alt"></i>
              </b-button>
            </span>
          </div>
          <div class="ocr-doc-view">
            <iframe v-if="previewKind === 'pdf'" :src="`${previewUrl}#navpanes=0&view=FitH`" title="เอกสารต้นฉบับ" class="ocr-doc-pdf"></iframe>
            <img v-else :src="previewUrl" alt="เอกสารต้นฉบับ" :style="{ width: `${zoom}%` }" />
          </div>
        </section>

        <section class="ocr-form">
          <p class="text-muted small">
            ตรวจข้อมูลที่ระบบอ่านได้เทียบกับเอกสาร{{ previewUrl ? "ด้านซ้าย" : "" }} แก้ให้ถูกก่อนกดยืนยันบันทึก
          </p>
          <div class="ocr-head">
            <b-form-group label="เลขที่คำสั่งจ่าย" label-size="sm">
              <b-form-input v-model.trim="statementRef" placeholder="เช่น 120069MD021900034269" size="sm" />
            </b-form-group>
            <b-form-group label="ยอดเงินรวมในเอกสาร (บาท)" label-size="sm">
              <b-form-input v-model.number="depositAmount" type="number" step="0.01" size="sm" />
            </b-form-group>
            <b-form-group label="วันที่ในเอกสาร" label-size="sm">
              <b-form-input v-model="statementDate" type="date" size="sm" />
            </b-form-group>
          </div>

          <b-alert :show="checksumFailCount > 0" variant="warning" class="small py-2">
            เลขบัตรประชาชน {{ checksumFailCount }} แถวไม่ถูกต้อง อาจอ่านจากเอกสารผิด
            กรุณาเทียบกับเอกสารก่อนบันทึก
          </b-alert>
          <b-alert :show="!!totalMismatch" variant="warning" class="small py-2">
            <template v-if="totalMismatch">
              ยอดรวมรายการ {{ formatAmount(totalMismatch.linesTotal) }} บาท
              ไม่ตรงกับยอดในเอกสาร {{ formatAmount(totalMismatch.depositAmount) }} บาท
            </template>
          </b-alert>

          <b-table
            hover
            small
            head-variant="light"
            :items="lines"
            :fields="fields"
            responsive
            show-empty
            empty-text="ไม่พบรายการจากเอกสาร"
          >
            <template #cell(index)="row">
              <span class="text-muted">{{ row.index + 1 }}</span>
            </template>
            <template #cell(cid)="row">
              <b-form-input
                v-model.trim="row.item.cid"
                size="sm"
                placeholder="เลขบัตร ปชช."
                :state="cidFailsChecksum(row.item.cid) ? false : null"
              />
              <small v-if="cidFailsChecksum(row.item.cid)" class="text-danger">เลขบัตรไม่ถูกต้อง</small>
            </template>
            <template #cell(patientName)="row">
              <b-form-input v-model.trim="row.item.patientName" size="sm" placeholder="ชื่อ-สกุล" />
            </template>
            <template #cell(serviceDate)="row">
              <b-form-input v-model="row.item.serviceDate" type="date" size="sm" />
            </template>
            <template #cell(amount)="row">
              <b-form-input v-model.number="row.item.amount" type="number" step="0.01" size="sm" class="text-right" />
            </template>
            <template #cell(buttons)="row">
              <b-button variant="outline-danger" size="sm" title="ลบแถวนี้" @click="removeLine(row.index)">
                <i class="fas fa-trash-alt"></i>
              </b-button>
            </template>
          </b-table>
        </section>
      </div>
    </b-modal>
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Dialog from "@/components/modal/Dialog.vue";
import { isValidThaiCid, linesTotalMismatch, apiErrorMessage, scanPreviewKind } from "@/utils/reconciliation";

const ZOOM_MIN = 50;
const ZOOM_MAX = 300;
const ZOOM_STEP = 25;

export default {
  name: "OcrReviewModal",
  components: { Dialog },
  props: {
    payer: { type: String, required: true },
  },
  data() {
    return {
      statementRef: "",
      depositAmount: null,
      statementDate: null,
      fileKey: null,
      lines: [],
      isSubmitting: false,
      // object URL of the uploaded file (revoked when the modal closes)
      previewUrl: null,
      previewKind: null,
      zoom: 100,
      ZOOM_MIN,
      ZOOM_MAX,
      ZOOM_STEP,
      fields: [
        { key: "index", label: "#" },
        { key: "cid", label: "เลขบัตร ปชช." },
        { key: "patientName", label: "ชื่อ-สกุล" },
        { key: "serviceDate", label: "วันที่ใช้สิทธิ" },
        { key: "amount", label: "จำนวนเงิน", class: "text-right" },
        { key: "buttons", label: "", class: "text-center" },
      ],
    };
  },
  beforeDestroy() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },
  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    checksumFailCount() {
      return this.lines.filter((l) => this.cidFailsChecksum(l.cid)).length;
    },
    totalMismatch() {
      return linesTotalMismatch(this.lines, this.depositAmount);
    },
  },
  methods: {
    ...mapActions("moduleReconciliation", ["commitReviewed"]),
    // Called by the parent upload control right after ocrExtract resolves. Every
    // per-row field the table below binds to (cid/serviceDate/amount/patientName)
    // must be declared here at object-creation time — Vue 2 won't make a field
    // added later reactive, and these rows arrive straight from an HTTP response
    // into component data (same discipline as setOutstanding's `box` field).
    // `file` is the uploaded scan, shown beside the rows for comparison.
    open(extracted, file = null) {
      this.previewKind = scanPreviewKind(file);
      this.previewUrl = this.previewKind ? URL.createObjectURL(file) : null;
      this.zoom = 100;
      this.statementRef = extracted?.statementRef || "";
      this.depositAmount = extracted?.depositAmount != null ? Number(extracted.depositAmount) : null;
      this.statementDate = extracted?.statementDate || null;
      this.fileKey = extracted?.fileKey || null;
      this.lines = (extracted?.lines || []).map((l) => ({
        cid: l.cid || "",
        serviceDate: l.serviceDate || "",
        amount: l.amount != null ? Number(l.amount) : null,
        patientName: l.patientName || "",
      }));
      this.$refs.modal.show();
    },
    reset() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.previewKind = null;
      this.statementRef = "";
      this.depositAmount = null;
      this.statementDate = null;
      this.fileKey = null;
      this.lines = [];
    },
    // Lets staff discard a hallucinated/unreadable OCR row instead of having to
    // hand-fix every field or abandon the whole batch. `lines` is unsorted and
    // unfiltered in this table, so row.index lines up with the array index.
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    // Warn-only: a 13-digit CID that fails the check digit is most likely an OCR misread, but
    // staff may still commit it (the line then lands in รอตรวจสอบ for a manual match).
    cidFailsChecksum(cid) {
      return this.isValidCid(cid) && !isValidThaiCid(cid.trim());
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isValidCid(cid) {
      return typeof cid === "string" && /^\d{13}$/.test(cid.trim());
    },
    isValidServiceDate(serviceDate) {
      return typeof serviceDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(serviceDate);
    },
    isValidAmount(amount) {
      return typeof amount === "number" && isFinite(amount) && amount > 0;
    },
    // depositAmount is nullable in CommitReviewed.yaml (type: number, nullable: true) — clearing
    // the OCR-prefilled value via v-model.number leaves it as an empty string "" (Vue's .number
    // modifier falls back to the raw string when parseFloat("") is NaN), which is neither a
    // number nor null and 400s at the OAS validator. Normalize anything that isn't a finite
    // number to null so an intentionally-blank field still commits (statementRef above is
    // required so it blocks submit instead; depositAmount is optional so it normalizes).
    normalizedDepositAmount() {
      return typeof this.depositAmount === "number" && isFinite(this.depositAmount) ? this.depositAmount : null;
    },
    // The backend OAS validator runs in strict mode: cid/serviceDate/amount are
    // all required, serviceDate must be format:date, amount must be type:number.
    // A single bad row currently 400s the ENTIRE commit with a generic toast, so
    // catch it here with a message that names the offending row/field.
    validateLines() {
      for (let i = 0; i < this.lines.length; i++) {
        const line = this.lines[i];
        const rowNo = i + 1;
        if (!this.isValidCid(line.cid)) {
          return `แถวที่ ${rowNo}: เลขบัตรประชาชนไม่ถูกต้อง กรุณาระบุตัวเลข 13 หลัก`;
        }
        if (!this.isValidServiceDate(line.serviceDate)) {
          return `แถวที่ ${rowNo}: กรุณาระบุวันที่ใช้สิทธิให้ถูกต้อง`;
        }
        if (!this.isValidAmount(line.amount)) {
          return `แถวที่ ${rowNo}: จำนวนเงินไม่ถูกต้อง กรุณาระบุตัวเลขมากกว่า 0`;
        }
      }
      return null;
    },
    async submit(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (this.isSubmitting) return;
      if (this.lines.length === 0) {
        this.$refs.Dialog.showToast("danger", "ไม่มีรายการให้บันทึก");
        return;
      }
      // statementRef is required (not nullable) by CommitReviewed.yaml and also
      // powers the duplicate-upload guard server-side — a poor scan can leave it
      // blank, so block submit here instead of a bare 400.
      if (!this.statementRef || !this.statementRef.trim()) {
        this.$refs.Dialog.showToast("danger", "กรุณาระบุเลขที่คำสั่งจ่าย (statement ref)");
        return;
      }
      const lineError = this.validateLines();
      if (lineError) {
        this.$refs.Dialog.showToast("danger", lineError);
        return;
      }
      this.isSubmitting = true;
      try {
        const res = await this.commitReviewed({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          payload: {
            payer: this.payer,
            statementRef: this.statementRef || null,
            depositAmount: this.normalizedDepositAmount(),
            statementDate: this.statementDate || null,
            fileKey: this.fileKey,
            lines: this.lines.map((l) => ({
              cid: l.cid,
              serviceDate: l.serviceDate,
              amount: l.amount,
              patientName: l.patientName,
            })),
          },
        });
        this.$refs.modal.hide();
        this.$emit("committed", res.data);
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "บันทึกรายการไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style>
/* the modal is portaled to <body>, outside this component's scope */
.ocr-review-dialog {
  max-width: min(1600px, 96vw);
}
</style>

<style scoped>
.ocr-layout {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 16px;
}
.ocr-layout.no-preview {
  grid-template-columns: minmax(0, 1fr);
}
.ocr-doc {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
  overflow: hidden;
  position: sticky;
  top: 0;
  height: 72vh;
}
.ocr-doc-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e9ef;
}
.zoom-label {
  min-width: 40px;
  text-align: center;
}
.ocr-doc-view {
  flex: 1;
  overflow: auto;
  background: #eef1f5;
}
.ocr-doc-view img {
  display: block;
  max-width: none;
}
.ocr-doc-pdf {
  width: 100%;
  height: 100%;
  border: 0;
}
.ocr-head {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 10px;
}
@media (max-width: 991px) {
  .ocr-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .ocr-doc {
    position: static;
    height: 50vh;
  }
  .ocr-head {
    grid-template-columns: 1fr;
  }
}
</style>
